import React, { useCallback, useMemo, useState, useContext } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import getCroppedImg from "./getCroppedImg";
import CropperModalforprof from "./CropperModalforprof";
import { useAPI } from "../context/ApiContext";
import { SnackbarContext } from "../context/SnackbarContext";

export const ASPECT_RATIO = 1 / 1;
export const CROP_WIDTH = 400;

const DEFAULT_AVATAR = "/assets/images/default_profile.png";

const useStyles = makeStyles(() => ({
  root: {
    marginTop: 30,
    minWidth: "100%",
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    flexFlow: "column",
    "& .file-upload-container": {
      width: 500,
      maxWidth: "100%",
      marginTop: 10,
      "& .button": {
        backgroundColor: "#00A0FF",
        color: "white",
      },
    },
    "& .img-container": {
      marginTop: 40,
      width: `${CROP_WIDTH}px`,
      height: `${CROP_WIDTH / ASPECT_RATIO}px`,
      display: "flex",
      alignItems: "center",
      borderRadius: 5,
      border: "1px solid #ccc",
      overflow: "hidden",
      backgroundColor: "#F7F7F7",
      "& .img": { width: "100%", objectFit: "contain" },
      "& .no-img": {
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#888",
      },
    },
  },
}));

/** 画像を指定サイズ以下に圧縮（JPEG 品質を段階的に落とす） */
const compressImage = (imageBlob, maxSize) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(imageBlob);
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      let quality = 0.9;
      const step = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("compress failed"));
            if (blob.size <= maxSize || quality < 0.1) resolve(blob);
            else {
              quality -= 0.1;
              step();
            }
          },
          "image/jpeg",
          quality
        );
      };
      step();
    };
    img.onerror = (err) => reject(err);
  });

const parseBirthday = (iso) => {
  if (!iso) return { y: "", m: "", d: "" };
  const [y, m, d] = String(iso).split("-");
  return { y: y || "", m: m || "", d: d || "" };
};

const AccountSettings = () => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const { newSnack } = useContext(SnackbarContext);

  const { profile, me, updateMyProfile } = useAPI();

  // 既存プロフィールから初期値を構築
  const initial = useMemo(() => {
    const bd = parseBirthday(profile?.birthday);
    return {
      nickname: profile?.nickname || "",
      caption: profile?.caption || "",
      birthYear: bd.y,
      birthMonth: bd.m ? String(parseInt(bd.m, 10)) : "",
      birthDay: bd.d ? String(parseInt(bd.d, 10)) : "",
    };
  }, [profile]);

  const [nickname, setNickname] = useState(initial.nickname);
  const [caption, setCaption] = useState(initial.caption);
  const [birthYear, setBirthYear] = useState(initial.birthYear);
  const [birthMonth, setBirthMonth] = useState(initial.birthMonth);
  const [birthDay, setBirthDay] = useState(initial.birthDay);

  // Cropping modal
  const [cropOpen, setCropOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // 送信用
  const [imageFile, setImageFile] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 121 }, (_, i) => String(now - i));
  }, []);
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => String(i + 1)), []);
  const days = useMemo(() => Array.from({ length: 31 }, (_, i) => String(i + 1)), []);

  const onFileChange = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result?.toString() || "";
      setImgSrc(url);
      setCropOpen(true);
    };
    reader.readAsDataURL(f);
  }, []);

  const onMediaLoaded = useCallback((mediaSize) => {
    const { width, height } = mediaSize;
    const mediaAspect = width / height;
    if (mediaAspect > ASPECT_RATIO) {
      const result = CROP_WIDTH / ASPECT_RATIO / height;
      setZoom(result);
      setMinZoom(result);
      return;
    }
    const result = CROP_WIDTH / width;
    setZoom(result);
    setMinZoom(result);
  }, []);

  const applyCroppedImage = useCallback(async () => {
    if (!croppedAreaPixels || !imgSrc) return;
    setIsUpdating(true);
    try {
      const croppedBlob = await getCroppedImg(imgSrc, croppedAreaPixels);
      let finalBlob = croppedBlob;
      try {
        // 200KB 目安で圧縮（必要なければそのまま）
        finalBlob = await compressImage(croppedBlob, 200 * 1024);
      } catch {/* 圧縮失敗時は元を使う */}
      const file = new File([finalBlob], "avatar.jpg", { type: "image/jpeg" });
      setImageFile(file);
      newSnack("success", t("AvatarReady") || "新しいアイコンを適用します（保存で反映）");
    } catch (e) {
      newSnack("error", t("AvatarFailed") || "画像の処理に失敗しました");
    } finally {
      setIsUpdating(false);
      setCropOpen(false);
      setImgSrc("");
    }
  }, [croppedAreaPixels, imgSrc, newSnack, t]);

  const buildBirthdayISO = () => {
    if (!birthYear || !birthMonth || !birthDay) return "";
    const mm = String(birthMonth).padStart(2, "0");
    const dd = String(birthDay).padStart(2, "0");
    return `${birthYear}-${mm}-${dd}`;
  };

  const canSubmit = useMemo(() => {
    const n = (nickname || "").trim();
    const hasBirthday = !!buildBirthdayISO();
    return n.length > 0 && hasBirthday && !isUpdating;
  }, [nickname, birthYear, birthMonth, birthDay, isUpdating]);

  const onSave = useCallback(async () => {
    if (!canSubmit) {
      newSnack("warning", t("AccountEach11") || "必須項目が未入力です（ニックネーム / 生年月日）");
      return;
    }
    setIsUpdating(true);
    try {
      await updateMyProfile({
        nickname: nickname.trim(),
        birthday: buildBirthdayISO(),
        caption: caption || "",
        image: imageFile || undefined, // 未変更なら送らない
      });
      newSnack("success", t("Saved") || "保存しました");
      // 自分のプロフィールへ
      const uid = me?.userId;
      if (uid) history.push(`/user/${encodeURIComponent(uid)}`);
      else history.push("/settings");
    } catch (e) {
      newSnack("error", e?.data?.detail || t("SaveFailed") || "保存に失敗しました");
    } finally {
      setIsUpdating(false);
    }
  }, [canSubmit, updateMyProfile, nickname, caption, imageFile, me?.userId, history, newSnack, t]);

  const createdAtText = useMemo(() => {
    const c = profile?.created_at;
    if (!c) return "";
    try {
      const d = new Date(c);
      return isNaN(d.getTime()) ? String(c) : d.toLocaleDateString();
    } catch {
      return String(c);
    }
  }, [profile?.created_at]);

  return (
    <div className="main-content bg-lightblue theme-dark-bg right-chat-active">
      <div className="middle-sidebar-bottom">
        <div className="middle-sidebar-left">
          <div className="middle-wrap">
            <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
              {/* ヘッダー行 */}
              <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3">
                <Link to="/settings" className="d-inline-block mt-2">
                  <i className="ti-arrow-left font-sm text-white" />
                </Link>
                <h4 className="font-xs text-white fw-600 ms-4 mb-0 mt-2">
                  {t("AccountEach1") || "アカウント設定"}
                </h4>
              </div>

              {/* 本体 */}
              <div className="card-body p-lg-5 p-4 w-100 border-0">
                {/* 上段：アバター＋基本 */}
                <div className="row justify-content-center">
                  <div className="col-lg-4 text-center">
                    <figure className="avatar ms-auto me-auto mb-0 mt-2 w100">
                      {isUpdating ? (
                        <div className="d-flex align-items-center justify-content-center" style={{ height: 120 }}>
                          <CircularProgress size={28} />
                        </div>
                      ) : (
                        <img
                          src={imageFile ? URL.createObjectURL(imageFile) : (profile?.image || DEFAULT_AVATAR)}
                          alt="avatar"
                          className="shadow-sm rounded-3 w-100"
                          onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
                        />
                      )}
                    </figure>

                    <h2 className="fw-700 font-sm text-grey-900 mt-3">
                      {profile?.nickname || t("NoName") || "未設定"}
                    </h2>
                    {createdAtText && (
                      <h4 className="text-grey-500 fw-500 mb-3 font-xsss mb-4">
                        {createdAtText}
                      </h4>
                    )}
                  </div>
                </div>

                {/* 画像アップロード */}
                <div className="row justify-content-center">
                  <div className="col-lg-12">
                    <div className={classes.root}>
                      <div className="file-upload-container">
                        <Button
                          className="bg-current text-center text-white font-xsss fw-600 pt-2 pb-2 mb-3 w175 rounded-3 d-inline-block"
                          variant="contained"
                          component="label"
                          disabled={isUpdating}
                        >
                          {t("AccountEach2") || "プロフィール画像を変更"}
                          <input type="file" hidden accept="image/*" onChange={onFileChange} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cropper モーダル */}
                <CropperModalforprof
                  crop={crop}
                  setCrop={setCrop}
                  zoom={zoom}
                  setZoom={setZoom}
                  onCropComplete={(_c, p) => setCroppedAreaPixels(p)}
                  open={cropOpen}
                  onClose={() => setCropOpen(false)}
                  imgSrc={imgSrc}
                  showCroppedImage={applyCroppedImage}
                  onMediaLoaded={onMediaLoaded}
                  minZoom={minZoom}
                />

                {/* 入力フォーム */}
                <form className="mt-3" onSubmit={(e) => { e.preventDefault(); onSave(); }}>
                  <div className="row">
                    {/* ニックネーム */}
                    <div className="col-lg-12 mb-3">
                      <div className="form-group">
                        <label className="mont-font fw-600 font-xsss mb-2">
                          {t("AccountEach4") || "ニックネーム"}
                          <span className="text-danger font-xs"> *</span>
                        </label>
                        <input
                          type="text"
                          maxLength={20}
                          className={`form-control ${!nickname?.trim() ? "border-danger" : ""}`}
                          value={nickname}
                          onChange={(e) => setNickname(e.target.value)}
                          placeholder={t("Nickname") || "ニックネーム"}
                        />
                      </div>
                    </div>

                    {/* 生年月日（年・月・日） */}
                    <div className="col-lg-12 mb-3">
                      <div className="form-group">
                        <label className="mont-font fw-600 font-xsss mb-2">
                          {t("AccountEach5") || "生年月日"}
                          <span className="text-danger font-xs"> *</span>
                        </label>
                        <div className="d-flex">
                          <select
                            className={`form-select me-1 font-xss ${!birthYear ? "border-danger" : ""}`}
                            value={birthYear}
                            onChange={(e) => setBirthYear(e.target.value)}
                          >
                            <option value="">{t("AccountEach12") || "年"}</option>
                            {years.map((y) => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>

                          <select
                            className={`form-select me-1 ms-1 font-xss ${!birthMonth ? "border-danger" : ""}`}
                            value={birthMonth}
                            onChange={(e) => setBirthMonth(e.target.value)}
                          >
                            <option value="">{t("AccountEach13") || "月"}</option>
                            {months.map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>

                          <select
                            className={`form-select ms-1 font-xss ${!birthDay ? "border-danger" : ""}`}
                            value={birthDay}
                            onChange={(e) => setBirthDay(e.target.value)}
                          >
                            <option value="">{t("AccountEach14") || "日"}</option>
                            {days.map((d) => (
                              <option key={d} value={d}>{d}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* 自己紹介 */}
                    <div className="col-lg-12 mb-3">
                      <label className="mont-font fw-600 font-xsss mb-2 text-dark">
                        {t("AccountEach7") || "自己紹介"}
                      </label>
                      <textarea
                        className="form-control mb-0 p-3 h100 bg-greylight lh-16 font-xss"
                        maxLength={200}
                        rows={5}
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        placeholder={t("AccountEach18") || "自己紹介を入力してください"}
                      />
                    </div>

                    {/* 保存ボタン */}
                    <div className="card-body w-100 border-0">
                      <div className="row justify-content-center mb-2">
                        <div className="col-lg-4 text-center">
                          <Button
                            className="bg-current text-center text-white font-xsss fw-600 p-3 w175 rounded-3 ms-1 me-1 mt-2"
                            variant="contained"
                            onClick={onSave}
                            disabled={!canSubmit}
                          >
                            {t("Save") || "保存"}
                          </Button>
                          {!canSubmit && (
                            <div className="text-danger font-xssss mt-2">
                              {t("AccountEach11") || "ニックネームと生年月日は必須です"}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
